export class IACall {
  webhookUrl = "https://automation.maguitech.com/webhook/citas";

  // Elementos del DOM
  elements!: {
    call: {
      callInterface: HTMLElement;
      micButton: HTMLElement;
      hangupButton: HTMLElement;
      conversation: HTMLElement;
      status: HTMLElement;
    };
    email: {
      emailModal: HTMLElement;
      emailForm: HTMLElement;
      emailError: HTMLElement;
      emailInput: HTMLInputElement;
    };
  };

  // Propiedades reconocimiento de voz
  recognition: SpeechRecognition | null = null;
  isRecording: boolean = false;
  accumulatedText: string = "";
  sessionId: string | null = null;
  userEmail: string = "";
  isSpeechRecognitionSupported: boolean = false;
  isSpeechSynthesisSupported: boolean = false;
  browser: string = "";
  version: string | undefined = undefined;
  voices: SpeechSynthesisVoice[] = [];

  constructor() {
    // Verificamos compatibilidad del navegador
    this.setBrowserSpeechOptions();

    // Inicializamos elements del DOM
    this.initDOMElements();

    // Inicializar control de las cajas y botones
    this.toogleCallBox();

    // Inicializar ID de sesión
    this.getSessionId();

    // Cargamos las voces disponibles
    this.getBrowserVoices();

    // Inicializamos los eventos de los botones
    this.setEventListeners();
  }

  /**
   * Inicializa los elementos del DOM necesarios para la funcionalidad de la aplicación.
   *
   * Esta función selecciona y asigna los elementos del DOM a las propiedades correspondientes
   * dentro del objeto `elements`. Estos elementos incluyen componentes relacionados con la interfaz
   * de llamada y el formulario de correo electrónico.
   *
   * @remarks
   * - Asegúrate de que los elementos del DOM existan en el HTML antes de llamar a esta función.
   * - Si algún elemento no se encuentra, se lanzará un error debido al uso de `as HTMLElement`.
   */
  private initDOMElements() {
    this.elements = {
      call: {
        callInterface: document.querySelector(".call-interface") as HTMLElement,
        micButton: document.querySelector("#micButton") as HTMLElement,
        hangupButton: document.querySelector("#hangupButton") as HTMLElement,
        conversation: document.querySelector("#conversation") as HTMLElement,
        status: document.querySelector("#status") as HTMLElement,
      },
      email: {
        emailModal: document.querySelector("#emailModal") as HTMLElement,
        emailForm: document.querySelector("#emailForm") as HTMLFormElement,
        emailError: document.querySelector("#emailError") as HTMLElement,
        emailInput: document.querySelector("#emailInput") as HTMLInputElement,
      },
    };
  }

  /**
   * Inicializa el botón de llamada y su funcionalidad
   * Oculta la interfaz de llamada al cargar la página
   * Cambia el estilo del botón de llamada y el texto al hacer clic
   */
  private toogleCallBox() {
    const callButtonImg = document.querySelector(
      ".ai-call-link__img"
    ) as HTMLElement;
    const callButtonText = document.querySelector(
      ".ai-call-link__text"
    ) as HTMLElement;
    const chatButtonImg = document.querySelector(
      ".chatbot-link__img"
    ) as HTMLElement;
    const chatButtonText = document.querySelector(
      ".chatbot-link__text"
    ) as HTMLElement;

    const toggleAI = document.querySelector(".ai-call-link") as HTMLElement;
    const interfaceIA = document.querySelector(
      ".call-interface"
    ) as HTMLElement;

    interfaceIA.style.display = "none";

    toggleAI.addEventListener("click", () => {
      if (interfaceIA.style.display != "none") {
        console.log("toggleAI click");
        if (this.recognition) {
          console.log("Recognition existe y se para. ", this.recognition);
          this.recognition.stop();
          this.isRecording = false;
        }
      } else {
        // Si se abre la interfaceIA, se oculta el chatbot
        const interfaceChatbot = document.querySelector(
          ".chat-window"
        ) as HTMLElement;
        if (interfaceChatbot) {
          interfaceChatbot.style.display = "none";
        }
      }

      interfaceIA.style.display =
        interfaceIA.style.display === "none" ? "flex" : "none";
      callButtonImg.classList.toggle("active");
      callButtonText.classList.toggle("active");
      chatButtonImg.classList.remove("active");
      chatButtonText.classList.remove("active");
    });
  }

  /**
   * Genera un nuevo identificador de sesión único.
   *
   * @returns {string} Un ID de sesión en formato "session_<timestamp>_<cadena_aleatoria>".
   * El ID se crea combinando la marca de tiempo actual y una cadena aleatoria generada.
   * Este método asegura que se genere un nuevo ID cada vez que se inicia la aplicación.
   */
  private getSessionId() {
    // Crear un ID nuevo cada vez que se inicia la aplicación
    const sessionId =
      "session_" +
      Date.now() +
      "_" +
      Math.random().toString(36).substring(2, 9);
    return sessionId;
  }

  /**
   * Obtiene las voces disponibles en el navegador utilizando la API de síntesis de voz (SpeechSynthesis).
   * Si la funcionalidad de síntesis de voz está disponible en el navegador, se registra un evento
   * para cargar las voces cuando cambien.
   *
   * @remarks
   * Esta función verifica si la API de `speechSynthesis` está disponible en el objeto `window`.
   * Si está disponible, asigna las voces obtenidas a la propiedad `this.voices` y las imprime en la consola.
   */
  private getBrowserVoices() {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        this.voices = window.speechSynthesis.getVoices();
        console.log("Voces cargadas:", this.voices);
      };
    }
  }

  /**
   * Verifica la disponibilidad de las funcionalidades de llamada
   * como el reconocimiento y la síntesis de voz en el navegador actual.
   * También identifica el navegador y su versión.
   *
   * @returns Un objeto con información del navegador y compatibilidad.
   */
  private browserSpeechOptions(): {
    speechRecognitionSupported: boolean;
    speechSynthesisSupported: boolean;
    browser: string;
    version: string | undefined;
  } {
    const userAgent = navigator.userAgent;
    const browserInfo = (() => {
      if (userAgent.includes("Chrome")) {
        return {
          name: "Chrome",
          version: userAgent.match(/Chrome\/(\d+)/)?.[1],
        };
      } else if (userAgent.includes("Firefox")) {
        return {
          name: "Firefox",
          version: userAgent.match(/Firefox\/(\d+)/)?.[1],
        };
      } else if (
        userAgent.includes("Safari") &&
        !userAgent.includes("Chrome")
      ) {
        return {
          name: "Safari",
          version: userAgent.match(/Version\/(\d+)/)?.[1],
        };
      } else if (userAgent.includes("Edge")) {
        return { name: "Edge", version: userAgent.match(/Edg\/(\d+)/)?.[1] };
      } else {
        return { name: "Unknown", version: "Unknown" };
      }
    })();

    const speechRecognitionSupported =
      "webkitSpeechRecognition" in window || "SpeechRecognition" in window;
    const speechSynthesisSupported = "speechSynthesis" in window;

    return {
      speechRecognitionSupported,
      speechSynthesisSupported,
      browser: browserInfo.name,
      version: browserInfo.version,
    };
  }

  /**
   * Inicializa una instancia de reconocimiento de voz y síntesis de voz si el navegador lo soporta.
   *
   * @param callBrowserAvailable - Objeto que indica las capacidades del navegador relacionadas con
   * el reconocimiento y la síntesis de voz, así como información del navegador y su versión.
   * @param callBrowserAvailable.speechRecognitionSupported - Indica si el navegador soporta el reconocimiento de voz.
   * @param callBrowserAvailable.speechSynthesisSupported - Indica si el navegador soporta la síntesis de voz.
   * @param callBrowserAvailable.browser - Nombre del navegador.
   * @param callBrowserAvailable.version - Versión del navegador.
   */
  private initRecognitionInstance(callBrowserAvailable: {
    speechRecognitionSupported: boolean;
    speechSynthesisSupported: boolean;
    browser: string;
    version?: string;
  }) {
    console.log("Creando instancia de reconocimiento de voz...");
    if (
      callBrowserAvailable.speechRecognitionSupported &&
      callBrowserAvailable.speechSynthesisSupported
    ) {
      this.isSpeechRecognitionSupported =
        callBrowserAvailable.speechRecognitionSupported;
      this.isSpeechSynthesisSupported =
        callBrowserAvailable.speechSynthesisSupported;
      this.recognition = new (window.SpeechRecognition ||
        window.webkitSpeechRecognition)();
      this.recognition.lang = "es-ES";
      this.recognition.maxAlternatives = 1;
    }
  }

  private setEventListeners() {
    // Botón de llamada
    this.elements.call.micButton.addEventListener("click", () => {
      console.log("Botón de micrófono presionado");
      this.startRecordingIfEmailExists();
    });

    // Botón de colgar
    this.elements.call.hangupButton.addEventListener("click", () => {
      this.elements.call.hangupButton.addEventListener("click", () => {
        console.log("Colgando llamada...");
        if (this.isRecording && this.recognition) {
          this.isRecording = false;
          this.recognition.stop();
          console.log("Grabación detenida");
        }

        if (this.isSpeechSynthesisSupported) {
          window.speechSynthesis.cancel();
          console.log("Síntesis de voz cancelada");
        }
      });
    });

    // Formulario de correo electrónico
    this.elements.email.emailForm.addEventListener("submit", (event) => {
      event.preventDefault();

      const email = this.elements.email.emailInput.value.trim();
      if (!this.validateEmail(email)) {
        this.setEmailError(
          "Por favor, introduce un correo electrónico válido."
        );
        return;
      }

      // Enviar el correo al webhook en lugar de guardarlo en localStorage
      this.registerEmail(email);
    });
  }

  /**
   * Registra un correo electrónico enviando los datos al webhook configurado.
   *
   * @param email - El correo electrónico que se desea registrar.
   *
   * @remarks
   * Esta función realiza las siguientes acciones:
   * - Muestra un mensaje de estado indicando que se está registrando el correo.
   * - Envía una solicitud POST al webhook con los datos del correo y otros detalles.
   * - Si la solicitud es exitosa, actualiza el estado del sistema, cierra el modal de correo,
   *   y añade un mensaje del sistema confirmando el registro.
   * - Configura los eventos para los botones de micrófono y colgar.
   * - Maneja errores mostrando un mensaje de error en caso de que el registro falle.
   *
   * @throws {Error} Si la respuesta del servidor no es exitosa.
   */
  private registerEmail(email: string) {
    this.showStatus("Registrando correo electrónico...");

    // Datos para enviar
    const data = {
      mensaje: `Registro de correo electrónico: ${email}`,
      timestamp: new Date().toISOString(),
      tipoSolicitud: "registro_email",
      esperaRespuesta: true,
      sessionId: this.sessionId,
      especialista: "citas",
      email: email,
    };

    // Enviar al webhook
    fetch(this.webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Error en la respuesta: " + response.status);
        }
        return response.json();
      })
      .then((data) => {
        console.log("Correo registrado:", data);
        this.showStatus("Correo electrónico registrado correctamente.");

        // Continuar con el chat
        this.userEmail = email;
        this.closeEmailModal();

        // Mensaje de bienvenida
        this.addAIMessage(
          "Bienvenido al servicio de Citas Telefónicas. ¿En qué puedo ayudarte hoy?"
        );
        this.speakText(
          "Bienvenido al servicio de Citas Telefónicas. ¿En qué puedo ayudarte hoy?"
        );

        // Añadir mensaje del sistema sobre el email
        this.addAIMessage(
          `Correo electrónico registrado: ${email}. ¿En qué podemos ayudarte?`
        );
        this.speakText(
          "Correo electrónico registrado correctamente. ¿En qué podemos ayudarte?"
        );
      })
      .catch((error) => {
        console.error("Error al registrar email:", error);
        this.elements.email.emailError.textContent =
          "Error al registrar el correo. Inténtalo de nuevo.";
      });
  }

  /**
   * Valida si una dirección de correo electrónico tiene un formato correcto.
   *
   * @param email - La dirección de correo electrónico a validar.
   * @returns `true` si el correo electrónico tiene un formato válido, de lo contrario `false`.
   */
  private validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Muestra un mensaje de estado en el elemento correspondiente, si está disponible.
   *
   * @param text - El texto que se mostrará como estado.
   */
  private showStatus(text: string) {
    if (!this.elements.call.status) return;
    this.elements.call.status.textContent = text;
  }

  /**
   * Determina si el dispositivo actual es un dispositivo móvil basado en la cadena del user agent.
   *
   * @returns {boolean} `true` si el dispositivo es un dispositivo móvil, de lo contrario `false`.
   */
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Cierra el modal de correo electrónico y limpia los mensajes de error.
   */
  private closeEmailModal() {
    if (!this.elements.email.emailModal) {
      return;
    }
    this.elements.email.emailModal.style.display = "none";
    if (this.elements.email.emailError) {
      this.elements.email.emailError.textContent = "";
    }
  }

  /**
   * Agrega un mensaje de la IA a la conversación en la interfaz de usuario.
   *
   * @param text - El texto del mensaje que se mostrará como respuesta de la IA.
   */
  private addAIMessage(text: string) {
    const messageDiv = document.createElement("div");
    messageDiv.className = "message ai-message";
    messageDiv.textContent = text;
    this.elements.call.conversation.appendChild(messageDiv);
    this.scrollConversationToBottom();
  }

  /**
   * Reproduce un texto dado utilizando la síntesis de voz del navegador.
   *
   * Esta función verifica si la síntesis de voz está soportada en el navegador.
   * Si está disponible, cancela cualquier síntesis en curso y reproduce el texto
   * proporcionado con una voz en español, preferiblemente femenina y de Google,
   * si está disponible.
   *
   * @param text - El texto que se desea reproducir mediante síntesis de voz.
   */
  private speakText(text: string) {
    if (!this.isSpeechSynthesisSupported) {
      return;
    }

    // Cancelar cualquier síntesis anterior
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "es-ES";

    // Seleccionar una voz femenina en español si está disponible
    let spanishVoice = this.voices.find(
      (voice) => voice.name.includes("Google") && voice.lang.includes("es")
    );
    if (!spanishVoice) {
      spanishVoice =
        this.voices.find(
          (voice) => voice.lang.includes("es") && voice.name.includes("Female")
        ) || this.voices.find((voice) => voice.lang.includes("es"));
    }

    if (spanishVoice) {
      utterance.voice = spanishVoice;
    }

    window.speechSynthesis.speak(utterance);
  }

  /**
   * Inicia la grabación si el correo electrónico del usuario ya está disponible.
   *
   * - Si el correo electrónico del usuario (`userEmail`) está definido, se inicia la grabación automáticamente.
   * - En caso contrario, se muestra un modal para solicitar el correo electrónico al usuario.
   */
  private startRecordingIfEmailExists() {
    // Si ya tenemos el email, iniciar grabación
    if (this.userEmail) {
      this.startRecording();
    } else {
      // Mostrar el modal para solicitar email
      this.showEmailModal();
    }
  }

  /**
   * Muestra el modal de correo electrónico en la interfaz de usuario.
   *
   * Esta función realiza las siguientes acciones:
   * - Cambia el estilo del modal de correo electrónico para que sea visible.
   * - Establece el foco en el campo de entrada de correo electrónico.
   * - Limpia cualquier mensaje de error previamente mostrado en el campo de error de correo electrónico.
   */
  private showEmailModal() {
    this.elements.email.emailModal.style.display = "block";
    this.elements.email.emailInput.focus();
    this.elements.email.emailError.textContent = "";
  }

  /**
   * Inicia o detiene la grabación de voz utilizando el reconocimiento de voz.
   *
   * - Si el reconocimiento de voz no está inicializado, intenta inicializarlo.
   * - Si no se está grabando, comienza la grabación, actualiza el estado visual del botón
   *   del micrófono y muestra un mensaje de estado.
   * - Si ya se está grabando, detiene la grabación y muestra un mensaje de estado indicando
   *   que se está procesando.
   *
   * @throws Muestra un mensaje de error en la consola y en la interfaz si ocurre un problema
   *         al iniciar el reconocimiento de voz.
   */
  private startRecording() {
    console.log("Iniciando grabación de voz...");
    console.log("Reconocimiento de voz:", this.recognition);
    if (!this.recognition && !this.initSpeechRecognition()) {
      return;
    }

    if (!this.isRecording) {
      // Iniciar grabación
      try {
        this.isRecording = true;
        this.recognition?.start();
        this.elements.call.micButton.classList.add("recording");
        this.accumulatedText = ""; // Reiniciar texto acumulado
        this.showStatus(
          "Escuchando..." +
            (this.isMobileDevice() ? " (Habla todo lo que necesites)" : "")
        );
      } catch (error) {
        console.error("Error al iniciar reconocimiento:", error);
        this.showStatus("Error al iniciar el micrófono. Intenta de nuevo.");
      }
    } else {
      // Detener grabación
      this.isRecording = false;
      this.recognition?.stop();
      this.showStatus("Procesando...");
    }
  }

  /**
   * Inicializa el reconocimiento de voz para la aplicación.
   *
   * - Verifica si el navegador soporta la funcionalidad de reconocimiento de voz.
   * - Configura el reconocimiento de voz según el tipo de dispositivo (móvil o escritorio).
   * - Maneja eventos como resultados, finalización y errores durante el reconocimiento.
   *
   * @returns {boolean} `true` si el reconocimiento de voz se inicializó correctamente, de lo contrario `false`.
   */
  private initSpeechRecognition(): boolean {
    // Crear instancia de reconocimiento de voz
    this.initRecognitionInstance(this.browserSpeechOptions());

    if (!this.isSpeechRecognitionSupported) {
      this.showStatus("Tu navegador no soporta reconocimiento de voz");
      return false;
    }
    if (!this.recognition) {
      this.showStatus("Error al inicializar el reconocimiento de voz");
      return false;
    }

    // Configuración optimizada según el tipo de dispositivo
    if (this.isMobileDevice()) {
      console.log("Dispositivo móvil detectado");
      // Para móviles: capturar en segmentos cortos para mejor precisión
      this.recognition.continuous = false;
      this.recognition.interimResults = true;

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        console.log("initSpeechRecognition onResult");
        let interimTranscript = "";
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += transcript;
            this.accumulatedText += " " + transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (interimTranscript) {
          this.showStatus(
            "Escuchando: " + this.accumulatedText + " " + interimTranscript
          );
        } else if (finalTranscript) {
          this.showStatus("Escuchando: " + this.accumulatedText);
        }
      };

      this.recognition.onend = () => {
        console.log("initSpeechRecognition onEnd");
        if (this.isRecording) {
          // Reiniciar automáticamente para continuar la grabación
          setTimeout(() => {
            try {
              this.recognition?.start();
              this.showStatus("Continúa hablando...");
            } catch (e) {
              console.error("Error al reiniciar reconocimiento:", e);
              this.isRecording = false;
              this.elements.call.micButton.classList.remove("recording");
            }
          }, 300);
        } else {
          // Enviar el texto acumulado cuando el usuario detiene la grabación
          if (this.accumulatedText.trim()) {
            const textToSend = this.accumulatedText.trim();
            this.addUserMessage(textToSend);
            this.sendToIA(textToSend);
            this.accumulatedText = "";
          }
          this.elements.call.micButton.classList.remove("recording");
          this.showStatus("");
        }
      };
    } else {
      console.log("Dispositivo de escritorio detectado");
      // Para desktop: grabación continua
      this.recognition.continuous = true;
      this.recognition.interimResults = true;

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        console.log("initSpeechRecognition onResult");
        let interimTranscript = "";
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          this.addUserMessage(finalTranscript);
          this.sendToIA(finalTranscript);
          this.recognition?.stop();
        }

        if (interimTranscript) {
          this.showStatus("Escuchando: " + interimTranscript);
        }
      };

      this.recognition.onend = () => {
        console.log("initSpeechRecognition onEnd");
        this.isRecording = false;
        this.elements.call.micButton.classList.remove("recording");
        this.showStatus("");
      };
    }

    // Manejo de errores mejorado
    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error("Error de reconocimiento:", event.error);

      if (
        this.isMobileDevice() &&
        this.isRecording &&
        ["no-speech", "network", "aborted"].includes(event.error)
      ) {
        // Reintentar en móviles para ciertos errores
        setTimeout(() => {
          if (this.isRecording) {
            try {
              this.recognition?.start();
              this.showStatus("Reconectando...");
            } catch (e) {
              console.error("Error al reiniciar tras error:", e);
              this.isRecording = false;
              this.elements.call.micButton.classList.remove("recording");
            }
          }
        }, 500);
      } else {
        this.showStatus("Error: " + event.error);
        this.isRecording = false;
        this.elements.call.micButton.classList.remove("recording");
      }
    };

    return true;
  }

  /**
   * Desplaza la conversación hacia abajo para mostrar el último mensaje.
   */
  private scrollConversationToBottom() {
    if (this.elements.call.conversation) {
      this.elements.call.conversation.scrollTop =
        this.elements.call.conversation.scrollHeight;
    }
  }

  /**
   * Agrega un mensaje de usuario al elemento de conversación en la interfaz.
   *
   * @param text - El texto del mensaje que se desea agregar.
   *
   * Esta función crea un nuevo elemento `<div>` con la clase `message user-message`,
   * asigna el texto proporcionado como contenido del elemento y lo agrega al contenedor
   * de conversación definido en `this.elementsCall.conversation`. Finalmente, asegura
   * que la conversación se desplace automáticamente hacia abajo para mostrar el nuevo mensaje.
   */
  private addUserMessage(text: string) {
    const messageDiv = document.createElement("div");
    messageDiv.className = "message user-message";
    messageDiv.textContent = text;
    this.elements.call.conversation.appendChild(messageDiv);
    this.scrollConversationToBottom();
  }

  /**
   * Envía un mensaje al servicio de IA a través de un webhook y procesa la respuesta.
   *
   * @param text - El texto del mensaje que se enviará al servicio de IA.
   *
   * El método realiza las siguientes acciones:
   * - Muestra un estado indicando que el mensaje está siendo enviado.
   * - Construye un objeto de datos que incluye información como el mensaje,
   *   la marca de tiempo, el tipo de solicitud, el ID de sesión, el especialista y el correo electrónico del usuario.
   * - Establece un tiempo máximo de espera de 30 segundos para la respuesta del servicio.
   * - Envía los datos al webhook utilizando una solicitud HTTP POST y maneja el tiempo de espera con `Promise.race`.
   * - Procesa la respuesta del servicio, mostrando el mensaje recibido o buscando respuestas en campos alternativos.
   * - En caso de error, muestra un mensaje indicando que hubo un problema al conectar con el servicio.
   *
   * @throws {Error} Si el tiempo de espera se agota o si la respuesta del servicio no es exitosa.
   */
  private sendToIA(text: string) {
    this.showStatus("Enviando mensaje...");

    // Datos para enviar
    const data = {
      mensaje: text,
      timestamp: new Date().toISOString(),
      tipoSolicitud: "cita_telefonica",
      esperaRespuesta: true,
      sessionId: this.sessionId,
      especialista: "citas",
      email: this.userEmail, // Incluimos el email del usuario
    };

    // Tiempo máximo de espera (30 segundos)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Tiempo de espera agotado")), 30000);
    });

    // Enviar al webhook con timeout
    Promise.race([
      fetch(this.webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }),
      timeoutPromise,
    ])
      .then((response: any) => {
        if (!response.ok) {
          throw new Error("Error en la respuesta: " + response.status);
        }
        return response.json();
      })
      .then((data) => {
        // Procesar la respuesta
        console.log("Respuesta recibida:", data);
        if (data && data.respuesta) {
          this.addAIMessage(data.respuesta);
          this.speakText(data.respuesta);
        } else {
          // Buscar la respuesta en campos alternativos
          const posibleRespuesta =
            data.output ||
            data.text ||
            (typeof data === "string" ? data : null);

          if (posibleRespuesta) {
            this.addAIMessage(posibleRespuesta);
            this.speakText(posibleRespuesta);
          } else {
            this.addAIMessage(
              "Lo siento, no pude procesar tu solicitud correctamente."
            );
          }
        }
        this.showStatus("");
      })
      .catch((error) => {
        console.error("Error:", error);
        this.addAIMessage(
          "Lo siento, hubo un problema al conectar con el servicio. Por favor, inténtalo de nuevo."
        );
        this.showStatus("");
      });
  }

  /**
   * Inicializa la funcionalidad de llamada de IA, configurando el reconocimiento de voz,
   * la síntesis de voz y el manejo del formulario de correo electrónico.
   *
   * - Verifica si el navegador soporta reconocimiento y síntesis de voz antes de continuar.
   * - Configura un evento para manejar el envío del formulario de correo electrónico.
   * - Muestra errores en la consola si el navegador no soporta las funcionalidades necesarias.
   * - Escucha el evento `submit` del formulario de correo electrónico para validar y registrar el correo.
   */
  public initCall() {
    console.log("Iniciando llamada IA...");
    console.log(
      "Reconocimiento de voz disponible:",
      this.browserSpeechOptions()
    );

    if (!this.isSpeechRecognitionSupported) {
      console.error(
        "El reconocimiento de voz no es compatible con este navegador."
      );
      return;
    }
    if (!this.isSpeechSynthesisSupported) {
      console.error("La síntesis de voz no es compatible con este navegador.");
      return;
    }
  }

  /**
   * Establece un mensaje de error en el elemento de error del correo electrónico.
   *
   * @param text - El texto del mensaje de error que se mostrará.
   *
   * Si el elemento de error del correo electrónico no está definido,
   * la función no realiza ninguna acción.
   */
  private setEmailError(text: string) {
    if (!this.elements.email.emailError) return;
    this.elements.email.emailError.textContent = text;
  }

  /**
   * Configura las opciones de reconocimiento y síntesis de voz del navegador.
   *
   * Este método obtiene las opciones del navegador relacionadas con el soporte
   * de reconocimiento y síntesis de voz, y las asigna a las propiedades correspondientes
   * de la clase. También identifica el navegador y su versión.
   *
   * Propiedades actualizadas:
   * - `isSpeechRecognitionSupported`: Indica si el navegador soporta el reconocimiento de voz.
   * - `isSpeechSynthesisSupported`: Indica si el navegador soporta la síntesis de voz.
   * - `browser`: Nombre del navegador detectado.
   * - `version`: Versión del navegador detectado.
   */
  private setBrowserSpeechOptions() {
    const browserOptions = this.browserSpeechOptions();
    this.isSpeechRecognitionSupported =
      browserOptions.speechRecognitionSupported;
    this.isSpeechSynthesisSupported = browserOptions.speechSynthesisSupported;
    this.browser = browserOptions.browser;
    this.version = browserOptions.version;
  }
}
